import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import {AccordionSummary, Typography} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {useTranslation} from "react-i18next";
import Flex from "components/grid/Flex";
import * as colors from "components/colors/Colors";

export const POSITIVE_CHIP_COLORS = {color: colors.clinicalPositiveText, background: colors.clinicalPositiveBackground};
export const WARNING_CHIP_COLORS = {color: colors.clinicalWarningText, background: colors.clinicalWarningBackground};
export const CRITICAL_CHIP_COLORS = {color: colors.clinicalCriticalText, background: colors.clinicalCriticalBackground};
export const INFO_CHIP_COLORS = {color: colors.clinicalInfoText, background: colors.clinicalInfoBackground};
export const NEUTRAL_CHIP_COLORS = {color: colors.clinicalNeutralText, background: colors.clinicalNeutralBackground};

export const FINDING_ASSESSMENT_CHIP_COLORS = {
    UNREMARKABLE: POSITIVE_CHIP_COLORS,
    ABNORMAL: WARNING_CHIP_COLORS,
    CRITICAL: CRITICAL_CHIP_COLORS,
    PARTIALLY_ASSESSABLE: NEUTRAL_CHIP_COLORS,
};

export const COMPLETENESS_CHIP_COLORS = {
    COMPLETE: POSITIVE_CHIP_COLORS,
    INCOMPLETE: WARNING_CHIP_COLORS,
    UNCLEAR: NEUTRAL_CHIP_COLORS,
};

export const HINT_PRIORITY_CHIP_COLORS = {
    CRITICAL: CRITICAL_CHIP_COLORS,
    IMPORTANT: WARNING_CHIP_COLORS,
    INFORMATION: INFO_CHIP_COLORS,
};

export const CONFIDENCE_CHIP_COLORS = {
    HIGH: POSITIVE_CHIP_COLORS,
    MEDIUM: WARNING_CHIP_COLORS,
    LOW: NEUTRAL_CHIP_COLORS,
};

export const DIAGNOSIS_CERTAINTY_CHIP_COLORS = {
    CONFIRMED: POSITIVE_CHIP_COLORS,
    SUSPECTED: WARNING_CHIP_COLORS,
    EXCLUDED: NEUTRAL_CHIP_COLORS,
};

export const EVIDENCE_SOURCE_STATUS_CHIP_COLORS = {
    VERIFIED: POSITIVE_CHIP_COLORS,
    NOT_VERIFIED: NEUTRAL_CHIP_COLORS,
};

export const COMPARISON_DIRECTION_CHIP_COLORS = {
    IMPROVED: POSITIVE_CHIP_COLORS,
    DETERIORATED: CRITICAL_CHIP_COLORS,
    STABLE: NEUTRAL_CHIP_COLORS,
};

export const KEY_VALUE_ASSESSMENT_DOT_COLORS = {
    NORMAL: colors.clinicalPositiveText,
    BORDERLINE: colors.clinicalWarningText,
    ELEVATED: colors.clinicalCriticalText,
    DECREASED: colors.clinicalCriticalText,
    PATHOLOGICAL: colors.clinicalCriticalText,
    NOT_ASSESSABLE: colors.clinicalOutline,
};

const HINT_PRIORITY_ORDER = {CRITICAL: 0, IMPORTANT: 1, INFORMATION: 2};
const UNKNOWN_HINT_PRIORITY_ORDER = 3;

export const OVERALL_SUMMARY_LETTER_TEXT_KEY = "overallSummary";

export const asArray = (schemaArray) => (Array.isArray(schemaArray) ? schemaArray : []);

export const asText = (schemaText) => {
    if (typeof schemaText === "string") {
        return schemaText;
    }
    if (typeof schemaText === "number" || typeof schemaText === "boolean") {
        return String(schemaText);
    }
    return "";
};

export const formatSchemaDate = (schemaDate) => {
    const schemaDateAsText = asText(schemaDate);
    const schemaDateParts = schemaDateAsText.split("-");
    if (schemaDateParts.length === 3) {
        return `${schemaDateParts[2]}.${schemaDateParts[1]}.${schemaDateParts[0]}`;
    }
    if (schemaDateParts.length === 2) {
        return `${schemaDateParts[1]}/${schemaDateParts[0]}`;
    }
    return schemaDateAsText;
};

export const sortHintsByPriority = (aiHints) =>
    asArray(aiHints)
        .filter((aiHint) => aiHint && typeof aiHint === "object")
        .sort((firstAiHint, secondAiHint) =>
            (HINT_PRIORITY_ORDER[firstAiHint.priority] ?? UNKNOWN_HINT_PRIORITY_ORDER)
            - (HINT_PRIORITY_ORDER[secondAiHint.priority] ?? UNKNOWN_HINT_PRIORITY_ORDER));

export const findingLetterTextKey = (finding, findingIndex) => asText(finding?.findingId) || `F${findingIndex + 1}`;

export const comparisonLetterTextKey = (findingLetterTextKeyOfFinding) => `${findingLetterTextKeyOfFinding}-comparison`;

const preventAiContentCopy = (aiContentCopyEvent) => aiContentCopyEvent.preventDefault();

export const AI_CONTENT_LOCK_HANDLERS = {
    onCopy: preventAiContentCopy,
    onCut: preventAiContentCopy,
    onContextMenu: preventAiContentCopy,
    onDragStart: preventAiContentCopy,
};

const useStyles = makeStyles((theme) => ({
    chip: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        lineHeight: 1.6,
    },
    lockedBox: {
        flexDirection: "column",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        background: colors.clinicalNoteBackground,
        borderTop: `1px solid ${colors.clinicalNoteBorder}`,
        color: colors.clinicalNoteText,
        userSelect: "none",
    },
    lockedBoxHead: {
        justifyContent: "space-between",
        alignItems: "center",
    },
    lockedBoxTitle: {
        alignItems: "center",
        gap: theme.spacing(1),
        fontSize: "0.8rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
    },
    lockedBoxNotCopyable: {
        fontSize: "0.75rem",
        color: colors.clinicalMutedText,
    },
    lockIcon: {
        fontSize: "1rem",
    },
    hintRow: {
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: theme.spacing(1.5),
        rowGap: theme.spacing(0.5),
        alignItems: "start",
        padding: theme.spacing(1.5),
        background: colors.htmlWhite,
        border: `1px solid ${colors.clinicalNoteBorder}`,
        borderRadius: 8,
    },
    hintTitle: {
        fontWeight: 700,
    },
    hintBody: {
        gridColumn: 2,
        fontSize: "0.875rem",
    },
    hintRecommendationLabel: {
        fontWeight: 700,
    },
    pointList: {
        margin: 0,
        paddingLeft: theme.spacing(2.5),
        fontSize: "0.875rem",
    },
    pointReason: {
        color: colors.clinicalMutedText,
        fontSize: "0.8rem",
    },
    slimAccordionSummary: {
        minHeight: 0,
        padding: theme.spacing(0, 2.25),
        color: colors.clinicalMutedText,
        "&.Mui-expanded": {minHeight: 0},
        "&:hover": {color: colors.htmlBlack},
    },
    slimAccordionSummaryContent: {
        margin: theme.spacing(1.125, 0),
        "&.Mui-expanded": {margin: theme.spacing(1.125, 0)},
    },
    slimAccordionExpandButton: {
        padding: theme.spacing(0.5),
        color: "inherit",
    },
    slimAccordionSummaryText: {
        fontSize: "0.84rem",
        color: "inherit",
    },
}));

export const ClinicalReportSummaryChip = ({labelPrefix, schemaValue, chipColorsBySchemaValue, chipColors, label}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();
    const schemaValueAsText = asText(schemaValue);
    const chipLabel = label ?? (schemaValueAsText ? translate(`${labelPrefix}.${schemaValueAsText}`, {defaultValue: schemaValueAsText}) : "");
    if (!chipLabel) {
        return null;
    }
    const shownChipColors = chipColors ?? chipColorsBySchemaValue?.[schemaValueAsText] ?? NEUTRAL_CHIP_COLORS;
    return (
        <span className={classes.chip} style={shownChipColors}>
            {chipLabel}
        </span>
    );
};

export const LockedAiHintsBox = ({hasHints, children}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();
    if (!hasHints) {
        return null;
    }
    return (
        <Flex container className={classes.lockedBox} role={"note"} aria-readonly={"true"} {...AI_CONTENT_LOCK_HANDLERS}>
            <Flex container className={classes.lockedBoxHead}>
                <Flex container className={classes.lockedBoxTitle}>
                    <LockIcon className={classes.lockIcon} />
                    {translate("clinicalReportSummarizer.aiHints.title")}
                </Flex>
                <Typography className={classes.lockedBoxNotCopyable}>
                    {translate("clinicalReportSummarizer.aiHints.notCopyable")}
                </Typography>
            </Flex>
            {children}
        </Flex>
    );
};

export const SlimAccordionSummary = ({title}) => {
    const classes = useStyles();
    return (
        <AccordionSummary
            expandIcon={<ExpandMoreIcon fontSize={"small"} />}
            classes={{
                root: classes.slimAccordionSummary,
                content: classes.slimAccordionSummaryContent,
                expandIcon: classes.slimAccordionExpandButton,
            }}
        >
            <Typography className={classes.slimAccordionSummaryText}>{title}</Typography>
        </AccordionSummary>
    );
};

export const AiHintRow = ({chip, title, text, recommendation, children}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();
    return (
        <div className={classes.hintRow}>
            <div>{chip}</div>
            <Typography className={classes.hintTitle}>{title}</Typography>
            {text && <Typography className={classes.hintBody}>{text}</Typography>}
            {children && <div className={classes.hintBody}>{children}</div>}
            {recommendation && (
                <Typography className={classes.hintBody}>
                    <span className={classes.hintRecommendationLabel}>
                        {translate("clinicalReportSummarizer.aiHints.recommendation")}
                    </span>
                    {" "}{recommendation}
                </Typography>
            )}
        </div>
    );
};

export const AiHintRowFromSchema = ({aiHint}) => (
    <AiHintRow
        chip={
            <ClinicalReportSummaryChip
                labelPrefix={"clinicalReportSummarizer.hintPriority"}
                schemaValue={aiHint.priority}
                chipColorsBySchemaValue={HINT_PRIORITY_CHIP_COLORS}
            />
        }
        title={asText(aiHint.title)}
        text={asText(aiHint.text)}
        recommendation={asText(aiHint.recommendation)}
    />
);

export const SchemaPointList = ({schemaPoints}) => {
    const classes = useStyles();
    const validSchemaPoints = asArray(schemaPoints).filter((schemaPoint) => schemaPoint && typeof schemaPoint === "object");
    if (validSchemaPoints.length === 0) {
        return null;
    }
    return (
        <ul className={classes.pointList}>
            {validSchemaPoints.map((schemaPoint, schemaPointIndex) => (
                <li key={schemaPointIndex}>
                    {asText(schemaPoint.text)}
                    {asText(schemaPoint.timeframe) && ` (${asText(schemaPoint.timeframe)})`}
                    {asText(schemaPoint.reasoning) && (
                        <div className={classes.pointReason}>{asText(schemaPoint.reasoning)}</div>
                    )}
                </li>
            ))}
        </ul>
    );
};
